/**
 * Admin preview / "log in as student" session handoff: admin.genius.space -> this app.
 *
 * The admin panel writes a one-time `gs_admin_preview` cookie on the shared
 * `.genius.space` domain right before it opens a page here, so the tokens
 * never travel in the URL, where GTM/GA4 and Sentry would record them.
 * The payload has the `gs_handoff` shape (profile-new docs/DESIGN-SWITCH.md)
 * plus `aud`, the origin it is meant for: prod and dev share the cookie jar.
 *
 * No store or tokens.js imports on purpose: adminPreviewHandoffBoot.js runs
 * this first in main.js, before Echo, axios and the router read the token,
 * and it has to work under Jest.
 */

import { cookieDomainFor, buildCookieString } from "./designSwitch";

export const ADMIN_PREVIEW_COOKIE_NAME = "gs_admin_preview";

const MAX_CLOCK_SKEW_SECONDS = 120;
const TOKEN_TYPES = ["authorization", "refresh"];
const SESSION_KEYS = ["authorization_token", "refresh_token", "session-id"];

function readCookie(name) {
	const row = document.cookie.split("; ").find((entry) => entry.indexOf(`${name}=`) === 0);

	return row ? row.slice(name.length + 1) : null;
}

function clearCookie(name) {
	const secure = window.location.protocol === "https:";
	const domain = cookieDomainFor(window.location.hostname);

	// Clear both variants: clearing with the wrong Domain attribute is a no-op.
	document.cookie = buildCookieString({ name, value: "", maxAge: 0, domain: null, secure });

	if (domain) {
		document.cookie = buildCookieString({ name, value: "", maxAge: 0, domain, secure });
	}
}

function isNonEmptyString(value) {
	return typeof value === "string" && value !== "";
}

function findToken(tokens, type, nowSeconds) {
	const entry = Array.isArray(tokens) ? tokens.find((item) => item && item.type === type) : null;

	if (!entry || !isNonEmptyString(entry.token)) {
		return null;
	}

	const expiration = Number(entry.expiration);

	return Number.isFinite(expiration) && expiration > nowSeconds ? entry : null;
}

function isValidPayload(payload, origin, nowSeconds) {
	if (!payload || typeof payload !== "object" || payload.v !== 1) {
		return false;
	}

	if (payload.aud !== origin || !isNonEmptyString(payload.sessionId)) {
		return false;
	}

	const iat = Number(payload.iat);

	if (!Number.isFinite(iat) || Math.abs(nowSeconds - iat) > MAX_CLOCK_SKEW_SECONDS) {
		return false;
	}

	return TOKEN_TYPES.every((type) => findToken(payload.tokens, type, nowSeconds) !== null);
}

/**
 * Reads the handoff cookie and deletes it straight away, valid or not.
 * Never throws.
 *
 * @returns {{present: boolean, payload: object|null}}
 */
export function readAndClearAdminPreview() {
	let raw = null;

	try {
		raw = readCookie(ADMIN_PREVIEW_COOKIE_NAME);
	} catch (e) {
		return { present: false, payload: null };
	}

	if (raw === null) {
		return { present: false, payload: null };
	}

	try {
		clearCookie(ADMIN_PREVIEW_COOKIE_NAME);
	} catch (e) {
		// The cookie expires by itself in seconds.
	}

	try {
		const payload = JSON.parse(decodeURIComponent(raw));
		const nowSeconds = Math.floor(Date.now() / 1000);

		return { present: true, payload: isValidPayload(payload, window.location.origin, nowSeconds) ? payload : null };
	} catch (e) {
		return { present: true, payload: null };
	}
}

/**
 * Applies a handoff from readAndClearAdminPreview(). When a cookie was present,
 * the current session is dropped first, even if the cookie was invalid, so the
 * tab never silently stays under the previous user. Tokens are stored in the
 * same format as the AUTH_STORE_TOKEN mutation (store/modules/auth.js).
 *
 * @param {{present: boolean, payload: object|null}} result
 * @param {Storage} [storage]
 * @returns {boolean} true when a new session was stored
 */
export function applyAdminPreview(result, storage = window.localStorage) {
	if (!result || !result.present) {
		return false;
	}

	SESSION_KEYS.forEach((key) => storage.removeItem(key));

	if (!result.payload) {
		return false;
	}

	const nowSeconds = Math.floor(Date.now() / 1000);
	const entries = TOKEN_TYPES.map((type) => findToken(result.payload.tokens, type, nowSeconds));

	if (entries.indexOf(null) !== -1) {
		return false;
	}

	entries.forEach((entry) => {
		storage.setItem(`${entry.type}_token`, JSON.stringify({ type: entry.type, value: entry.token, expiration: Number(entry.expiration) }));
	});

	storage.setItem("session-id", result.payload.sessionId);

	return true;
}
